import React from "react";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./AdminStoreBanners.css";

const FALLBACK_BANNER = {
  id: "fallback-store-banner",
  url: "/Banner-Store.jpg",
  originalName: "Banner padrao da Store",
  size: 0,
};

const formatFileSize = (size) => {
  const value = Number(size);
  if (!Number.isFinite(value) || value <= 0) return "0 KB";
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
};

const AdminStoreBanners = () => {
  const [menuClose, setMenuClose] = React.useState(false);
  const [banners, setBanners] = React.useState([]);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [activePreview, setActivePreview] = React.useState(0);
  const fileInputRef = React.useRef(null);

  const toggleMenu = () => setMenuClose((prev) => !prev);

  const loadBanners = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await Api.get("/public/store-banners");
      setBanners(Array.isArray(response?.data?.items) ? response.data.items : []);
    } catch (err) {
      console.error("Erro ao carregar banners da store:", err);
      setError("Nao foi possivel carregar os banners da Store.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  React.useEffect(() => {
    setActivePreview(0);
  }, [banners]);

  React.useEffect(() => {
    if (banners.length <= 1) return undefined;

    const interval = window.setInterval(() => {
      setActivePreview((current) => (current + 1) % banners.length);
    }, 4500);

    return () => window.clearInterval(interval);
  }, [banners]);

  const handleSelectFiles = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setError("");
    setSuccess("");
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (selectedFiles.length === 0) {
      setError("Selecione ao menos uma imagem para enviar.");
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("images", file));

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const response = await Api.post("/admin/store-banners", formData, {
        timeout: 30000,
      });

      setBanners(Array.isArray(response?.data?.items) ? response.data.items : []);
      setSelectedFiles([]);
      setSuccess(
        "Banners enviados com sucesso. Se houver mais de um, a Store mostrara um carrossel."
      );

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Erro ao enviar banners da store:", err);
      setError(
        err?.code === "ECONNABORTED"
          ? "O envio demorou mais do que o esperado. Tente novamente."
          : err?.response?.data?.message || "Nao foi possivel enviar os banners da Store."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm("Deseja remover este banner da Store?")) return;

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      await Api.delete(`/admin/store-banners/${bannerId}`);
      setBanners((current) => current.filter((item) => item.id !== bannerId));
      setSuccess("Banner removido com sucesso.");
    } catch (err) {
      console.error("Erro ao remover banner da store:", err);
      setError(err?.response?.data?.message || "Nao foi possivel remover o banner da Store.");
    } finally {
      setSubmitting(false);
    }
  };

  const previewItems = banners.length > 0 ? banners : [FALLBACK_BANNER];

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} storeBannersActive="active" />

      <main className="main-content">
        <Topbar page="Store / Banners" onToggleMenu={toggleMenu} />

        <section className="admin-store-banners-grid">
          <article className="admin-store-panel">
            <div className="admin-store-header">
              <div>
                <h2>Banners da Store</h2>
                <p>
                  Envie novas imagens para o topo da Store. Quando houver mais de uma, o
                  licenciado vera um carrossel automaticamente.
                </p>
              </div>
              <span className="admin-store-badge">Super administrador</span>
            </div>

            <form className="admin-store-form" onSubmit={handleUpload}>
              <label className="admin-store-upload-box" htmlFor="store-banners">
                <span>Selecionar imagens da Store</span>
                <small>Formatos aceitos: JPG, PNG, WEBP. Limite de 10 MB por arquivo.</small>
              </label>

              <input
                ref={fileInputRef}
                id="store-banners"
                type="file"
                accept="image/*"
                multiple
                onChange={handleSelectFiles}
              />

              {selectedFiles.length > 0 ? (
                <div className="admin-store-selected-files">
                  {selectedFiles.map((file) => (
                    <div className="admin-store-selected-file" key={`${file.name}-${file.lastModified}`}>
                      <strong>{file.name}</strong>
                      <span>{formatFileSize(file.size)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="admin-store-help">
                  A ordem do carrossel segue a ordem de envio. Para reorganizar, remova e envie
                  novamente na sequencia desejada.
                </p>
              )}

              <div className="admin-store-actions">
                <button type="submit" disabled={submitting || selectedFiles.length === 0}>
                  {submitting ? "Enviando..." : "Enviar banners"}
                </button>
              </div>

              {error ? <div className="admin-store-message error">{error}</div> : null}
              {success ? <div className="admin-store-message success">{success}</div> : null}
            </form>
          </article>

          <article className="admin-store-panel">
            <div className="admin-store-header compact">
              <div>
                <h2>Preview da Store</h2>
                <p>
                  {loading
                    ? "Carregando imagens..."
                    : banners.length > 0
                      ? `${banners.length} banner(s) ativo(s)`
                      : "Sem banner cadastrado. O sistema usa o banner padrao."}
                </p>
              </div>
            </div>

            <div className="admin-store-preview">
              <div className="admin-store-preview-stage">
                {previewItems.map((banner, index) => (
                  <div
                    key={banner.id || `preview-${index}`}
                    className={`admin-store-preview-slide ${index === activePreview ? "is-active" : ""}`}
                  >
                    <img src={banner.url} alt={banner.originalName || `Banner ${index + 1}`} />
                  </div>
                ))}

                {previewItems.length > 1 ? (
                  <div className="admin-store-preview-dots">
                    {previewItems.map((banner, index) => (
                      <button
                        key={`preview-dot-${banner.id || index}`}
                        type="button"
                        className={index === activePreview ? "is-active" : ""}
                        onClick={() => setActivePreview(index)}
                        aria-label={`Exibir banner ${index + 1}`}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            {loading ? (
              <div className="admin-store-empty">Carregando banners...</div>
            ) : banners.length === 0 ? null : (
              <div className="admin-store-banner-grid">
                {banners.map((banner, index) => (
                  <article className="admin-store-banner-card" key={banner.id}>
                    <div className="admin-store-banner-image">
                      <img src={banner.url} alt={banner.originalName || `Banner ${index + 1}`} />
                    </div>

                    <div className="admin-store-banner-body">
                      <strong>{banner.originalName || `Banner ${index + 1}`}</strong>
                      <span>Posicao {index + 1}</span>
                      <span>{formatFileSize(banner.size)}</span>
                    </div>

                    <button
                      type="button"
                      className="admin-store-banner-remove"
                      onClick={() => handleDelete(banner.id)}
                      disabled={submitting}
                    >
                      Remover
                    </button>
                  </article>
                ))}
              </div>
            )}
          </article>
        </section>
      </main>
    </div>
  );
};

export default AdminStoreBanners;
